// src/services/expense.service.ts
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db, storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Expense, CreateExpenseDto, UpdateExpenseDto } from '../types/finance.types';

const COLLECTION_NAME = 'expenses';

const generateId = () => `exp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export async function createExpense(
  data: CreateExpenseDto,
  userId: string
): Promise<Expense> {
  try {
    const expenseId = generateId();
    const expense: Expense = {
      id: expenseId,
      description: data.description,
      amount: data.amount,
      categoryId: data.categoryId,
      categoryName: data.categoryName,
      date: Timestamp.fromDate(data.date),
      projectId: data.projectId,
      projectName: data.projectName,
      userId: data.userId,
      userName: data.userName,
      vendor: data.vendor,
      paymentMethod: data.paymentMethod,
      reimbursementStatus: data.reimbursementStatus,
      notes: data.notes,
      receiptUrl: data.receiptUrl,
      receiptFileName: data.receiptFileName,
      createdAt: Timestamp.now(),
      createdBy: userId,
      updatedAt: Timestamp.now(),
    };

    // Firestore rejects undefined; drop unset optional fields before writing.
    const clean = Object.fromEntries(Object.entries(expense).filter(([, v]) => v !== undefined)) as Expense;
    await setDoc(doc(db, COLLECTION_NAME, expenseId), clean);
    return expense;
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
}

export async function getExpenseById(expenseId: string): Promise<Expense | null> {
  try {
    const docRef = doc(db, COLLECTION_NAME, expenseId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as Expense;
    }
    return null;
  } catch (error) {
    console.error('Error getting expense:', error);
    throw error;
  }
}

export async function getExpenses(filters?: {
  projectId?: string;
  userId?: string;
  categoryId?: string;
  startDate?: Date;
  endDate?: Date;
}): Promise<Expense[]> {
  try {
    let q = query(collection(db, COLLECTION_NAME), orderBy('date', 'desc'));

    if (filters?.projectId) {
      q = query(q, where('projectId', '==', filters.projectId));
    }
    if (filters?.userId) {
      q = query(q, where('userId', '==', filters.userId));
    }
    if (filters?.categoryId) {
      q = query(q, where('categoryId', '==', filters.categoryId));
    }

    const snapshot = await getDocs(q);
    let expenses = snapshot.docs.map((doc) => doc.data() as Expense);

    // Client-side date filtering
    if (filters?.startDate) {
      expenses = expenses.filter((exp) => exp.date.toDate() >= filters.startDate!);
    }
    if (filters?.endDate) {
      expenses = expenses.filter((exp) => exp.date.toDate() <= filters.endDate!);
    }

    return expenses;
  } catch (error) {
    console.error('Error getting expenses:', error);
    throw error;
  }
}

export async function updateExpense(
  expenseId: string,
  data: UpdateExpenseDto
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, expenseId);
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    if (data.description !== undefined) updateData.description = data.description;
    if (data.amount !== undefined) updateData.amount = data.amount;
    if (data.categoryId !== undefined) updateData.categoryId = data.categoryId;
    if (data.categoryName !== undefined) updateData.categoryName = data.categoryName;
    if (data.date !== undefined) updateData.date = Timestamp.fromDate(data.date);
    if (data.projectId !== undefined) updateData.projectId = data.projectId;
    if (data.projectName !== undefined) updateData.projectName = data.projectName;
    if (data.vendor !== undefined) updateData.vendor = data.vendor;
    if (data.paymentMethod !== undefined) updateData.paymentMethod = data.paymentMethod;
    if (data.reimbursementStatus !== undefined) updateData.reimbursementStatus = data.reimbursementStatus;
    if (data.notes !== undefined) updateData.notes = data.notes;
    if (data.receiptUrl !== undefined) updateData.receiptUrl = data.receiptUrl;
    if (data.receiptFileName !== undefined) updateData.receiptFileName = data.receiptFileName;

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
}

export async function deleteExpense(expenseId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, expenseId));
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
}

export async function getExpenseSummaryByCategory(filters?: {
  projectId?: string;
  startDate?: Date;
  endDate?: Date;
}): Promise<{ categoryId: string; categoryName: string; total: number; count: number }[]> {
  try {
    const expenses = await getExpenses(filters);

    const categoryMap = new Map<
      string,
      { categoryId: string; categoryName: string; total: number; count: number }
    >();

    expenses.forEach((expense) => {
      const existing = categoryMap.get(expense.categoryId);
      if (existing) {
        existing.total += expense.amount;
        existing.count += 1;
      } else {
        categoryMap.set(expense.categoryId, {
          categoryId: expense.categoryId,
          categoryName: expense.categoryName,
          total: expense.amount,
          count: 1,
        });
      }
    });

    return Array.from(categoryMap.values()).sort((a, b) => b.total - a.total);
  } catch (error) {
    console.error('Error getting expense summary by category:', error);
    throw error;
  }
}

export async function getExpenseTotal(filters?: {
  projectId?: string;
  userId?: string;
  startDate?: Date;
  endDate?: Date;
}): Promise<number> {
  try {
    const expenses = await getExpenses(filters);
    return expenses.reduce((sum, exp) => sum + exp.amount, 0);
  } catch (error) {
    console.error('Error getting expense total:', error);
    throw error;
  }
}

export async function getMonthlyExpenseBreakdown(
  year: number,
  projectId?: string
): Promise<{ month: number; monthName: string; total: number }[]> {
  try {
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31, 23, 59, 59);

    const expenses = await getExpenses({
      projectId,
      startDate,
      endDate,
    });

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];

    const monthlyTotals = new Array(12).fill(0);

    expenses.forEach((expense) => {
      const month = expense.date.toDate().getMonth();
      monthlyTotals[month] += expense.amount;
    });

    return monthlyTotals.map((total, index) => ({
      month: index + 1,
      monthName: monthNames[index],
      total,
    }));
  } catch (error) {
    console.error('Error getting monthly expense breakdown:', error);
    throw error;
  }
}

export async function createExpenseFromLogistics(
  data: {
    description: string;
    amount: number;
    projectId: string;
    projectName: string;
    date: Date;
    sourceType: 'hotel' | 'flight' | 'rental_car' | 'ground_transport';
    sourceId: string;
  },
  userId: string,
  userName: string
): Promise<Expense> {
  try {
    const expenseId = generateId();
    const categoryMap = {
      flight: { id: 'travel_flight', name: 'Flights' },
      hotel: { id: 'travel_hotel', name: 'Hotels' },
      rental_car: { id: 'travel_rental', name: 'Car Rentals' },
      ground_transport: { id: 'other', name: 'Ground Transport' },
    };
    const category = categoryMap[data.sourceType];

    const expense: Expense = {
      id: expenseId,
      description: data.description,
      amount: data.amount,
      categoryId: category.id,
      categoryName: category.name,
      date: Timestamp.fromDate(data.date),
      projectId: data.projectId,
      projectName: data.projectName,
      userId: userId,
      userName: userName,
      vendor: '',
      notes: `Auto-created from logistics: ${data.sourceType}`,
      createdAt: Timestamp.now(),
      createdBy: userId,
      updatedAt: Timestamp.now(),
      sourceType: 'logistics',
      sourceId: data.sourceId,
    };

    await setDoc(doc(db, COLLECTION_NAME, expenseId), expense);
    return expense;
  } catch (error) {
    console.error('Error creating expense from logistics:', error);
    throw error;
  }
}

export async function createExpenseFromStaffPayment(
  data: {
    description: string;
    amount: number;
    projectId?: string;
    projectName?: string;
    date: Date;
    staffUserId: string;
    staffUserName: string;
    paymentId: string;
  },
  createdByUserId: string,
  createdByUserName: string
): Promise<Expense> {
  try {
    const expenseId = generateId();

    const expense: Expense = {
      id: expenseId,
      description: data.description,
      amount: data.amount,
      categoryId: 'payroll',
      categoryName: 'Staff Payments',
      date: Timestamp.fromDate(data.date),
      projectId: data.projectId || '',
      projectName: data.projectName || 'General',
      userId: data.staffUserId,
      userName: data.staffUserName,
      vendor: '',
      notes: `Staff payment: ${data.description}`,
      createdAt: Timestamp.now(),
      createdBy: createdByUserId,
      updatedAt: Timestamp.now(),
      sourceType: 'staff_payment',
      sourceId: data.paymentId,
    };

    await setDoc(doc(db, COLLECTION_NAME, expenseId), expense);
    return expense;
  } catch (error) {
    console.error('Error creating expense from staff payment:', error);
    throw error;
  }
}

export async function deleteExpensesBySource(sourceType: string, sourceId: string): Promise<void> {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('sourceType', '==', sourceType),
      where('sourceId', '==', sourceId)
    );
    const snapshot = await getDocs(q);

    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting expenses by source:', error);
    throw error;
  }
}

export async function updateExpenseBySource(
  sourceType: string,
  sourceId: string,
  updates: {
    description?: string;
    amount?: number;
    date?: Date;
    categoryId?: string;
    categoryName?: string;
  }
): Promise<void> {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('sourceType', '==', sourceType),
      where('sourceId', '==', sourceId)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return; // No linked expense to update
    }

    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.amount !== undefined) updateData.amount = updates.amount;
    if (updates.date !== undefined) updateData.date = Timestamp.fromDate(updates.date);
    if (updates.categoryId !== undefined) updateData.categoryId = updates.categoryId;
    if (updates.categoryName !== undefined) updateData.categoryName = updates.categoryName;

    const updatePromises = snapshot.docs.map(doc => updateDoc(doc.ref, updateData));
    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error updating expense by source:', error);
    throw error;
  }
}

export async function getProjectExpenseSummary(projectId: string): Promise<{
  totalExpenses: number;
  byCategory: { categoryId: string; categoryName: string; amount: number }[];
  byUser: { userId: string; userName: string; amount: number }[];
  recentExpenses: Expense[];
}> {
  try {
    const expenses = await getExpenses({ projectId });

    const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);

    // Group by category
    const categoryMap = new Map<string, { categoryId: string; categoryName: string; amount: number }>();
    expenses.forEach((exp) => {
      const existing = categoryMap.get(exp.categoryId);
      if (existing) {
        existing.amount += exp.amount;
      } else {
        categoryMap.set(exp.categoryId, {
          categoryId: exp.categoryId,
          categoryName: exp.categoryName,
          amount: exp.amount,
        });
      }
    });

    // Group by user
    const userMap = new Map<string, { userId: string; userName: string; amount: number }>();
    expenses.forEach((exp) => {
      const existing = userMap.get(exp.userId);
      if (existing) {
        existing.amount += exp.amount;
      } else {
        userMap.set(exp.userId, {
          userId: exp.userId,
          userName: exp.userName,
          amount: exp.amount,
        });
      }
    });

    return {
      totalExpenses,
      byCategory: Array.from(categoryMap.values()).sort((a, b) => b.amount - a.amount),
      byUser: Array.from(userMap.values()).sort((a, b) => b.amount - a.amount),
      recentExpenses: expenses.slice(0, 10),
    };
  } catch (error) {
    console.error('Error getting project expense summary:', error);
    throw error;
  }
}

/**
 * Upload a receipt file for an expense
 */
export async function uploadReceipt(
  expenseId: string,
  file: File
): Promise<{ url: string; fileName: string }> {
  try {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `receipt_${timestamp}.${ext}`;
    const storagePath = `expenses/${expenseId}/${fileName}`;

    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    // Update expense with receipt info
    await updateExpense(expenseId, {
      receiptUrl: url,
      receiptFileName: file.name
    });

    return { url, fileName: file.name };
  } catch (error) {
    console.error('Error uploading receipt:', error);
    throw error;
  }
}

/**
 * Delete a receipt from an expense
 */
export async function deleteReceipt(
  expenseId: string,
  receiptUrl: string
): Promise<void> {
  try {
    // Delete from storage
    const storageRef = ref(storage, receiptUrl);
    try {
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting receipt from storage (may not exist):', error);
    }

    // Update expense to remove receipt info
    await updateExpense(expenseId, {
      receiptUrl: undefined,
      receiptFileName: undefined
    });
  } catch (error) {
    console.error('Error deleting receipt:', error);
    throw error;
  }
}

export const expenseService = {
  createExpense,
  getExpenseById,
  getExpenses,
  updateExpense,
  deleteExpense,
  getExpenseSummaryByCategory,
  getExpenseTotal,
  getMonthlyExpenseBreakdown,
  createExpenseFromLogistics,
  createExpenseFromStaffPayment,
  deleteExpensesBySource,
  updateExpenseBySource,
  getProjectExpenseSummary,
  uploadReceipt,
  deleteReceipt,
};
